import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { maskNcm } from "@/pages/tax/exception-tax/utils/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Label } from "components/ui/label";
import { Switch } from "components/ui/switch";
import { toast } from "components/ui/use-toast";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { z } from "zod";
import { MESSAGES } from "../../utils/message";
import { CreateModalSchema } from "../../validation-form";
import { Info } from "lucide-react";

interface CreateModalProps {
  type: "NCM" | "Produto";
  onSave: (data: { ncm?: string; sku?: string; description: string }) => void;
}

export const CreateModal: React.FC<CreateModalProps> = ({ type, onSave }) => {
  const { handleModal } = useDialogModal();

  const form = useForm<z.infer<typeof CreateModalSchema>>({
    resolver: zodResolver(CreateModalSchema),
    mode: "onSubmit",
    defaultValues: { type },
  });

  const [applyToAll, setApplyToAll] = useState(false);
  const fieldName = type === "NCM" ? "ncm" : "sku";

  useEffect(() => {
    if (applyToAll) {
      form.setValue(fieldName, "TODOS", { shouldValidate: false });
    } else {
      form.setValue(fieldName, "", { shouldValidate: false });
    }
  }, [applyToAll, fieldName, form]);

  const onSubmit = (data: z.infer<typeof CreateModalSchema>) => {
    try {
      const finalData = {
        ...data,
        [fieldName]: applyToAll ? "all" : data[fieldName],
      };

      onSave(finalData);
      toast(MESSAGES.CREATE.SUCCESS(type));
    } catch (e) {
      toast(MESSAGES.CREATE.ERROR(type));
    }
  };

  const fieldError =
    form.formState.errors.ncm?.message || form.formState.errors.sku?.message;

  return (
    <div className="flex flex-col rounded-2xl w-[800px] max-w-full bg-gradient-to-br from-white to-gray-50 p-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name={fieldName as "ncm" | "sku"}
              render={({ field }) => (
                <FormItem className="w-full">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base font-semibold text-gray-800 flex items-center gap-2">
                      {type === "NCM" ? "NCM" : "SKU"}
                    </Label>

                    <div className="flex items-center gap-3 bg-yellow-50 px-4 py-2 rounded-lg border border-yellow-200">
                      <Switch
                        id="apply-all"
                        checked={applyToAll}
                        className="data-[state=checked]:bg-[#F5A524]"
                        onCheckedChange={(checked) => {
                          setApplyToAll(checked);
                          const label = type === "NCM" ? "NCMs" : "SKUs";
                          checked &&
                            toast({
                              title: "Atenção",
                              variant: "information",
                              description: `Essa exceção será aplicada para TODOS os ${label} do estado selecionado.`,
                            });
                        }}
                      />
                      <Label
                        htmlFor="apply-all"
                        className="text-sm font-semibold text-gray-700 cursor-pointer"
                      >
                        Aplicar para todos
                      </Label>
                    </div>
                  </div>

                  <div className="relative">
                    <Input
                      {...field}
                      disabled={applyToAll}
                      value={
                        applyToAll
                          ? "TODOS"
                          : type === "NCM"
                          ? maskNcm(field.value || "")
                          : field.value || ""
                      }
                      onChange={(e) =>
                        field.onChange(
                          type === "NCM"
                            ? maskNcm(e.target.value)
                            : e.target.value
                        )
                      }
                      className="w-full border-2 border-gray-200 rounded-xl p-4 text-base 
                        focus:border-[#F5A524] focus:ring-2 focus:ring-[#F5A524]/20 
                        transition-all duration-200 disabled:bg-gray-50 disabled:text-gray-500"
                      placeholder={`Digite o ${type === "NCM" ? "NCM" : "SKU"}`}
                    />
                    {applyToAll && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                          <Info className="w-3 h-3" />
                          Todos
                        </div>
                      </div>
                    )}
                  </div>
                  {fieldError && (
                    <FormMessage className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <span className="text-red-500">⚠</span> {fieldError}
                    </FormMessage>
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="w-full">
                  <Label className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    Descrição
                  </Label>
                  <Input
                    {...field}
                    className="w-full border-2 border-gray-200 rounded-xl p-4 text-base 
                      focus:border-[#F5A524] focus:ring-2 focus:ring-[#F5A524]/20 
                      transition-all duration-200"
                    placeholder="Digite a descrição do item"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-end items-center border-t border-gray-200 overflow-hidden gap-3 p-4">
            <Button
              type="button"
              variant="outline"
              className="border-2 border-gray-300 rounded-xl text-gray-700 bg-white 
                hover:bg-gray-50 hover:border-red-400 hover:text-red-500 hover:shadow-xl 
                transition-all duration-200 transform-gpu will-change-transform 
                px-6 py-3 font-medium shadow-sm"
              onClick={() => handleModal({ isOpen: false })}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="default"
              className=" px-6 py-3 text-base font-medium "
            >
              Salvar
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
